import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiEnvelope, AuthUser } from './models';

/**
 * Admin auth via httpOnly cookies (backend: POST /api/auth/signin with
 * { identifier, password }). We keep only a lightweight `user` signal in
 * memory; the browser holds the real credentials in the cookie.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private base = environment.apiBase;

  readonly user = signal<AuthUser | null>(null);
  readonly isAuthenticated = signal(false);

  signIn(identifier: string, password: string): Observable<ApiEnvelope<{ user: AuthUser }>> {
    return this.http
      .post<ApiEnvelope<{ user: AuthUser }>>(`${this.base}/auth/signin`, {
        identifier,
        password,
      })
      .pipe(tap((r) => this.setUser(r.data?.user ?? null)));
  }

  /** Verify an existing cookie session on app/admin load. */
  me(): Observable<ApiEnvelope<{ user: AuthUser }>> {
    return this.http
      .get<ApiEnvelope<{ user: AuthUser }>>(`${this.base}/auth/me`)
      .pipe(tap((r) => this.setUser(r.data?.user ?? null)));
  }

  logout(): Observable<ApiEnvelope<unknown>> {
    return this.http
      .post<ApiEnvelope<unknown>>(`${this.base}/auth/logout`, {})
      .pipe(tap(() => this.setUser(null)));
  }

  private setUser(user: AuthUser | null) {
    this.user.set(user);
    this.isAuthenticated.set(!!user);
  }
}
